<script setup>
import { computed, ref, watch } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import DeliveryUnknownsAPI from 'dashboard/api/teletel/deliveryUnknowns';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  VERIFICATION_METHODS,
  canConfirmDelivery,
  canRecoverEnqueue,
  deliveryErrorKey,
} from './deliveryUnknown';

const props = defineProps({
  messageId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
  delivery: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const role = useMapGetter('getCurrentRole');
const currentUserId = useMapGetter('getCurrentUserID');
const now = ref(Date.now());
const dialog = ref(null);
const viewedAttempt = ref('');
const verificationMethod = ref('');
const duplicateRiskAck = ref(false);
const busy = ref(false);
const error = ref('');
const accepted = ref(false);

useIntervalFn(() => {
  now.value = Date.now();
}, 1000);

const isUnknown = computed(() => props.delivery.state === 'unknown');
const isAuthorized = computed(() => props.delivery.state === 'authorized');
const canRecover = computed(() =>
  canRecoverEnqueue(props.delivery, role.value, currentUserId.value)
);
const canConfirm = computed(
  () =>
    !accepted.value && canConfirmDelivery(props.delivery, role.value, now.value)
);
const waitMinutes = computed(() => {
  const eligibleAt = Date.parse(props.delivery.eligibleAt);
  if (!Number.isFinite(eligibleAt)) return null;
  return Math.max(1, Math.ceil((eligibleAt - now.value) / 60000));
});
const validConfirmation = computed(
  () =>
    canConfirm.value &&
    viewedAttempt.value === props.delivery.attemptId &&
    VERIFICATION_METHODS.includes(verificationMethod.value) &&
    duplicateRiskAck.value &&
    !busy.value
);

const openConfirmation = () => {
  if (!canConfirm.value) return;
  viewedAttempt.value = props.delivery.attemptId;
  verificationMethod.value = '';
  duplicateRiskAck.value = false;
  error.value = '';
  dialog.value?.open();
};

const refreshAfterEnqueueFailure = async failure => {
  if (failure?.response?.data?.code !== 'TELETEL_META_ENQUEUE_FAILED') return;
  try {
    const { data } = await DeliveryUnknownsAPI.refreshMessage(
      props.conversationId,
      props.messageId,
      route?.params.accountId
    );
    const message = data.payload?.find(item => item.id === props.messageId);
    if (message) await store.dispatch('addMessage', message);
  } catch {
    // Keep the error and current state if the server read is unavailable.
  }
};

const recoverEnqueue = async () => {
  if (!canRecover.value || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const request =
      props.delivery.enqueueOrigin === 'human'
        ? DeliveryUnknownsAPI.confirmRetry(
            props.conversationId,
            props.messageId,
            {
              attempt_id: props.delivery.attemptId,
              verification_method: props.delivery.verificationMethod,
              duplicate_risk_ack: true,
            }
          )
        : DeliveryUnknownsAPI.retryKnownMessage(
            props.conversationId,
            props.messageId
          );
    const { data } = await request;
    await store.dispatch('addMessage', data);
  } catch (failure) {
    error.value = t(deliveryErrorKey(failure));
    await refreshAfterEnqueueFailure(failure);
  } finally {
    busy.value = false;
  }
};

const confirmRetry = async () => {
  if (!validConfirmation.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await DeliveryUnknownsAPI.confirmRetry(
      props.conversationId,
      props.messageId,
      {
        attempt_id: viewedAttempt.value,
        verification_method: verificationMethod.value,
        duplicate_risk_ack: true,
      }
    );
    // Keep the server's delivery state: the ordinary retry action optimistically
    // marks a message sent, which would conceal this attempt's uncertainty.
    await store.dispatch('addMessage', data);
    accepted.value = true;
    dialog.value?.close();
  } catch (failure) {
    error.value = t(deliveryErrorKey(failure));
    await refreshAfterEnqueueFailure(failure);
  } finally {
    busy.value = false;
  }
};

watch(
  () => [props.delivery.attemptId, props.delivery.state],
  () => {
    accepted.value = false;
    viewedAttempt.value = '';
    dialog.value?.close();
  }
);
</script>

<template>
  <section
    class="flex max-w-md flex-col gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 p-3 text-sm text-n-slate-12"
    aria-live="polite"
    data-testid="delivery-unknown"
  >
    <strong class="text-n-amber-11">
      {{
        t(
          isUnknown
            ? 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.TITLE'
            : isAuthorized
              ? 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.AUTHORIZED_TITLE'
              : 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.DUPLICATE_TITLE'
        )
      }}
    </strong>
    <p class="mb-0">
      {{
        t(
          isUnknown
            ? 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.EXPLANATION'
            : isAuthorized
              ? 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.AUTHORIZED_EXPLANATION'
              : 'TELETEL_DELIVERY_UNKNOWNS.MESSAGE.DUPLICATE_EXPLANATION'
        )
      }}
    </p>
    <template v-if="isAuthorized">
      <p v-if="delivery.enqueueRetryable" class="mb-0 text-n-slate-11">
        {{ t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.ENQUEUE_EXPLANATION') }}
      </p>
      <Button
        v-if="canRecover"
        size="sm"
        color="amber"
        :label="t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.REQUEUE')"
        :disabled="busy"
        data-testid="requeue-attempt"
        @click="recoverEnqueue"
      />
      <p v-if="error" role="alert" class="mb-0 text-sm text-n-ruby-11">
        {{ error }}
      </p>
    </template>
    <template v-if="isUnknown">
      <p v-if="accepted" class="mb-0 text-n-slate-11">
        {{ t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.APPROVED') }}
      </p>
      <p v-else-if="role !== 'administrator'" class="mb-0 text-n-slate-11">
        {{ t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.ASK_ADMIN') }}
      </p>
      <p v-else-if="!canConfirm" class="mb-0 text-n-slate-11">
        {{
          waitMinutes === null
            ? t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.WAIT_UNKNOWN')
            : t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.WAIT', {
                minutes: waitMinutes,
              })
        }}
      </p>
      <Button
        v-else
        size="sm"
        color="amber"
        :label="t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.RESEND')"
        :disabled="busy"
        @click="openConfirmation"
      />
      <Dialog
        ref="dialog"
        :title="t('TELETEL_DELIVERY_UNKNOWNS.CONFIRM.TITLE')"
        :description="t('TELETEL_DELIVERY_UNKNOWNS.CONFIRM.DESCRIPTION')"
        :confirm-button-label="t('TELETEL_DELIVERY_UNKNOWNS.MESSAGE.RESEND')"
        :disable-confirm-button="!validConfirmation"
        :is-loading="busy"
        @confirm="confirmRetry"
      >
        <label class="flex flex-col gap-2">
          <span>{{ t('TELETEL_DELIVERY_UNKNOWNS.CONFIRM.METHOD') }}</span>
          <select
            v-model="verificationMethod"
            class="mb-0 rounded-lg border border-n-weak bg-n-alpha-2 text-sm"
            :disabled="busy"
            data-testid="verification-method"
          >
            <option value="" disabled>
              {{ t('TELETEL_DELIVERY_UNKNOWNS.CONFIRM.PICK_METHOD') }}
            </option>
            <option
              v-for="method in VERIFICATION_METHODS"
              :key="method"
              :value="method"
            >
              {{ t(`TELETEL_DELIVERY_UNKNOWNS.CONFIRM.METHODS.${method}`) }}
            </option>
          </select>
        </label>
        <label class="flex items-start gap-2 text-sm">
          <input
            v-model="duplicateRiskAck"
            type="checkbox"
            class="mb-0 mt-1"
            :disabled="busy"
            data-testid="duplicate-risk-ack"
          />
          <span>{{ t('TELETEL_DELIVERY_UNKNOWNS.CONFIRM.ACK') }}</span>
        </label>
        <p v-if="error" role="alert" class="mb-0 text-sm text-n-ruby-11">
          {{ error }}
        </p>
      </Dialog>
    </template>
  </section>
</template>
